import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLogin } from "@features/auth/queries";
import { HttpError } from "@shared/http/client";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@features/auth/AuthContext";
import { useState } from "react";
import logoInstitucional from "/images/logo-institucional.png";

const loginSchema = z.object({
  username: z.string().min(1, "Ingresa tu correo o usuario"),
  password: z.string().min(1, "Ingresa tu contraseña"),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { user } = useAuth();
  const location = useLocation();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  });

  if (user) {
    const from = (location.state as { from?: string } | null)?.from || "/dashboard";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: LoginForm) => {
    try {
      await login.mutateAsync(values);
    } catch {
      /* error rendered from mutation.error */
    }
  };

  const errorMsg =
    login.error instanceof HttpError ? login.error.message : login.error ? "Error inesperado" : null;

  return (
    <div id="loginPage">
      <div className="login-left">
        <div className="login-left-grid"></div>
        <div className="login-left-content">
          <div className="login-hero-title">
            Sistema de Valoración<br />
            <span>Preoperatoria</span>
          </div>
          <div className="login-hero-sub">Gestión Clínica · Calidad</div>
          <div className="login-divider"></div>
          <div className="login-features">
            {[
              "Semaforización clínica",
              "Validación en tiempo real",
              "Control de acceso por roles",
            ].map((text) => (
              <div className="login-feature" key={text}>
                <div className="login-feature-check">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2.5 7l3 3 6-6"
                      stroke="#93C9EF"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card">
          <div className="login-institution">
            <img src={logoInstitucional} alt="Fundación Oftalmológica del Caribe" className="login-institution-logo-img" />
            <div className="login-institution-name">
              Fundación Oftalmológica
              <br />
              Del Caribe
            </div>
          </div>

          <div className="login-title">Bienvenido</div>
          <p className="login-subtitle">
            Ingresa tus credenciales para
            <br />
            acceder al sistema.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="login-fields">
              <div className="login-field">
                <label className="login-label" htmlFor="loginUsuario">Correo o usuario</label>
                <div className="login-input-wrap">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="login-input-icon">
                    <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="#B8CCE0" strokeWidth="1.5" />
                    <path
                      d="M2.5 4.5l5.5 4 5.5-4"
                      stroke="#B8CCE0"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    id="loginUsuario"
                    type="text"
                    placeholder="admin o tucorreo@dominio.com"
                    autoComplete="username"
                    aria-invalid={!!errors.username}
                    {...register("username")}
                  />
                </div>
                {errors.username && <div className="alert-inline alert-inline--danger mt-8">{errors.username.message}</div>}
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="loginClave">Contraseña</label>
                <div className="login-input-wrap">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="login-input-icon">
                    <rect x="3" y="7" width="10" height="7.5" rx="2" stroke="#B8CCE0" strokeWidth="1.5" />
                    <path d="M5.5 7V5.5a2.5 2.5 0 015 0V7" stroke="#B8CCE0" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="8" cy="10.5" r="1.2" fill="#B8CCE0" />
                  </svg>
                  <input
                    id="loginClave"
                    type={showPassword ? "text" : "password"}
                    placeholder="Contraseña"
                    autoComplete="current-password"
                    aria-invalid={!!errors.password}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: 0,
                      cursor: "pointer",
                      color: "#6B7C93",
                      fontSize: 14,
                    }}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                {errors.password && <div className="alert-inline alert-inline--danger mt-8">{errors.password.message}</div>}
              </div>
            </div>

            <button type="submit" className="btn-login" disabled={isSubmitting || login.isPending}>
              {login.isPending ? "Ingresando…" : "Ingresar"}
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M3.5 9h11M10 4.5l4.5 4.5-4.5 4.5"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {errorMsg && (
              <div className="alert-inline alert-inline--danger alert-inline--md alert-inline--bold mt-14">
                ❌ {errorMsg}
              </div>
            )}
          </form>

          <p className="footer-note">
            <a href="#" onClick={(e) => e.preventDefault()} className="link-brand">
              ¿Olvidaste tu contraseña?
            </a>
          </p>

          <div className="login-footer">© 2026 · Fundación Oftalmológica Del Caribe</div>
        </div>
      </div>
    </div>
  );
}
